import { Input } from '@thanx/uikit/input'
import { Text } from '@thanx/uikit/text'
import { TextArea } from '@thanx/uikit/textarea'
import { useStyletron } from '@thanx/uikit/theme'
import type { LiveUpdatesContentFieldKey } from 'api/liveUpdates.types'
import Lock from 'components/Icons/Lock'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React, { useRef } from 'react'
import { generateId } from 'utilities/uniqueId'

const t = buildTranslate('live_updates.card')

const LOCKED_BOX_PADDING = '10px 12px'
const COUNTER_FONT_SIZE = '12px'

// The insert-chip UI was pulled (design pending — AI-819), but a merchant can
// still hand-type this token into a cancellation's push message, and nucleus
// (OrderStatus::StatusConfiguration) still charges it 35 chars toward
// push_message_text's cap at publish time regardless of its literal length.
// Mirroring that here keeps the remaining-count from under-reporting and
// silently letting a merchant compose a message the server will reject.
const LOCATION_NAME_TOKEN = '{location_name}'
const LOCATION_NAME_TOKEN_PATTERN = /\{location_name\}/g
const VARIABLE_RENDERED_LENGTH = 35

const chargedLength = (
  text: string,
  fieldKey: LiveUpdatesContentFieldKey
): number => {
  if (fieldKey !== 'pushMessageText') return text.length
  const matches = text.match(LOCATION_NAME_TOKEN_PATTERN)
  if (!matches) return text.length
  return (
    text.length +
    matches.length * (VARIABLE_RENDERED_LENGTH - LOCATION_NAME_TOKEN.length)
  )
}

export interface MessageTextFieldProps {
  label: string
  fieldKey: LiveUpdatesContentFieldKey
  value: string
  placeholder?: string
  maxLength: number
  editable: boolean
  multiline?: boolean
  /** Present only for the Flybuy-managed push field — renders a locked box
   * instead of an input (FR: "Message managed by Flybuy"). */
  lockedLabel?: string
  lockedCaption?: string
  onChange: (value: string) => void
  /** Wired through so the preview panel can follow focus between fields. */
  onFocus?: () => void
  disabled?: boolean
}

// A single labeled message field for a Live Updates status card: live
// remaining-character count, input blocking at the limit (FR-70), and the
// Flybuy locked-field treatment.
const MessageTextField = ({
  label,
  fieldKey,
  value,
  placeholder,
  maxLength,
  editable,
  multiline = false,
  lockedLabel,
  lockedCaption,
  onChange,
  onFocus,
  disabled = false,
}: MessageTextFieldProps) => {
  const [css] = useStyletron()
  const uid = useRef(generateId()).current

  const isLocked = Boolean(lockedLabel)
  const charged = chargedLength(value, fieldKey)
  const remaining = maxLength - charged
  const isAtLimit = remaining <= 0

  const inputId = `${fieldKey}-message-field-${uid}`

  // FR-70: refuse edits that would push the charged length past maxLength —
  // but always allow a value to shrink.
  const handleChange = (newValue: string) => {
    const nextCharged = chargedLength(newValue, fieldKey)
    if (nextCharged <= maxLength || nextCharged < charged) {
      onChange(newValue)
    }
  }

  const handleFocus = () => onFocus?.()

  return (
    <div>
      <div
        className={css({
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          marginBottom: '4px',
        })}
      >
        <label htmlFor={inputId} className={css({ margin: 0 })}>
          <Text.BodyText4 bold color="grey90">
            {label}
          </Text.BodyText4>
        </label>
        {/* Figma: a Flybuy-locked field (no editable input) shows no count. */}
        {!isLocked && (
          <span
            aria-hidden="true"
            className={css({
              fontSize: COUNTER_FONT_SIZE,
              color: isAtLimit ? palette.cayenne : palette.grey50,
            })}
          >
            {remaining}
          </span>
        )}
      </div>

      {!isLocked && (
        <span role="status" aria-live="polite" className="sr-only">
          {remaining === 1
            ? t('fields.characters_remaining_one', { count: remaining })
            : t('fields.characters_remaining_other', { count: remaining })}
        </span>
      )}

      {isLocked ? (
        <div
          className={css({
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: LOCKED_BOX_PADDING,
            borderRadius: '4px',
            backgroundColor: palette.grey10,
          })}
        >
          <span
            aria-hidden="true"
            className={css({ display: 'flex', flexShrink: 0 })}
          >
            <Lock fillColor={palette.grey50} />
          </span>
          <Text.BodyText4 color="grey50">{lockedLabel}</Text.BodyText4>
        </div>
      ) : multiline ? (
        <TextArea
          id={inputId}
          value={value}
          placeholder={placeholder}
          disabled={disabled || !editable}
          error={isAtLimit}
          onChange={handleChange}
          onFocus={handleFocus}
        />
      ) : (
        // uikit-input: restores uikit's 1rem font-size, which loses specificity to bootstrap's global input[type='text'] rule (a rule TextArea isn't subject to)
        <Input
          id={inputId}
          className="uikit-input"
          value={value}
          placeholder={placeholder}
          disabled={disabled || !editable}
          error={isAtLimit}
          onChange={handleChange}
          onFocus={handleFocus}
        />
      )}

      {isLocked && lockedCaption && (
        <Text.BodyText5
          color="grey70"
          className={css({ display: 'block', marginTop: '4px' })}
        >
          {lockedCaption}
        </Text.BodyText5>
      )}
    </div>
  )
}

export default MessageTextField
